import { useCallback, useEffect, useRef, useState } from "react";
import { Room, RoomEvent } from "livekit-client";
import { liveavatar } from "@/lib/liveavatar/client";

type StreamState = "idle" | "loading" | "ready" | "connected" | "error";
const AVATAR_IDENTITY = "avatar";

interface UseLiveAvatarStreamOptions {
  onMessage?: (text: string) => void;
  onStateChange?: (state: StreamState) => void;
  onError?: (error: string) => void;
  onContextCreated?: (contextId: string) => void;
  useByoLivekit?: boolean;
}

interface PrepareSessionOptions {
  avatar_id?: string;
  context_id: string;
  voice_id?: string;
  language?: string;
  llm_configuration_id?: string;
  system_prompt?: string;
  opening_text?: string;
}

export function useLiveAvatarStream(options: UseLiveAvatarStreamOptions = {}) {
  const [state, setState] = useState<StreamState>("idle");
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const roomRef = useRef<Room | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const mediaResolverRef = useRef<((stream: MediaStream) => void) | null>(null);
  const byoParamsRef = useRef<PrepareSessionOptions | null>(null);
  const byoBootstrapRef = useRef<{ livekit_url: string; viewer_token: string; session_id: string | null } | null>(null);

  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const updateState = useCallback((s: StreamState) => {
    setState(s);
    optionsRef.current.onStateChange?.(s);
  }, []);

  const prepareSession = useCallback(
    async ({ avatar_id, context_id, voice_id, language, llm_configuration_id, system_prompt, opening_text }: PrepareSessionOptions) => {
      updateState("loading");
      sessionIdRef.current = null;
      byoBootstrapRef.current = null;
      // BYO mode: we mint LiveKit tokens + start the session in one call at
      // goLive time, so prepare just remembers the params.
      if (optionsRef.current.useByoLivekit) {
        byoParamsRef.current = { avatar_id, context_id, voice_id, language, llm_configuration_id, system_prompt, opening_text };
        sessionTokenRef.current = null;
        updateState("ready");
        return;
      }
      try {
        const data = await liveavatar.createSessionToken({
          avatar_id,
          context_id,
          voice_id,
          language: language || "en",
          ...(llm_configuration_id ? { llm_configuration_id } : {}),
        });
        if (!data?.session_token) {
          updateState("error");
          throw new Error("No session token returned");
        }
        sessionTokenRef.current = data.session_token;
        if (data.context_id) optionsRef.current.onContextCreated?.(data.context_id);
        updateState("ready");
      } catch (err) {
        updateState("error");
        throw err;
      }
    },
    [updateState],
  );

  const sendTextInternal = useCallback(async (text: string) => {
    // In BYO mode the Python agent worker drives the conversation via LLM,
    // so scripted send_text calls are a no-op.
    if (optionsRef.current.useByoLivekit) return;
    const token = sessionTokenRef.current;
    if (!token) return;
    const room = roomRef.current;
    if (room) {
      try {
        const payload = new TextEncoder().encode(JSON.stringify({ text }));
        await room.localParticipant.publishData(payload, { reliable: true });
        return;
      } catch (err) {
        console.warn("[stream] LiveKit data send failed, falling back to API:", err);
      }
    }
    try {
      await liveavatar.sendText(token, text);
    } catch (err) {
      console.warn("[stream] could not send text:", err);
    }
  }, []);

  const goLive = useCallback(
    async (openingText?: string) => {
      updateState("loading");

      let livekitUrl: string | undefined;
      let livekitToken: string | undefined;

      if (optionsRef.current.useByoLivekit) {
        const p = byoParamsRef.current;
        if (!p) {
          updateState("error");
          throw new Error("No session params — call prepareSession first");
        }
        const byo = await liveavatar.startByoSession({
          avatar_id: p.avatar_id,
          context_id: p.context_id,
          voice_id: p.voice_id,
          language: p.language || "en",
          ...(p.llm_configuration_id ? { llm_configuration_id: p.llm_configuration_id } : {}),
          ...(p.system_prompt ? { system_prompt: p.system_prompt } : {}),
          ...(p.opening_text ? { opening_text: p.opening_text } : {}),
        });
        if (!byo.agent_dispatch_id) {
          console.warn("[stream] BYO agent dispatch returned no id — worker may not pick up the job");
        }
        sessionTokenRef.current = byo.session_token;
        sessionIdRef.current = byo.session_id;
        byoBootstrapRef.current = {
          livekit_url: byo.livekit_url,
          viewer_token: byo.viewer_token,
          session_id: byo.session_id,
        };
        livekitUrl = byo.livekit_url;
        livekitToken = byo.viewer_token;
      } else {
        const token = sessionTokenRef.current;
        if (!token) {
          updateState("error");
          throw new Error("No session token — call prepareSession first");
        }
        const startData: any = await liveavatar.startSession(token);
        const payload = startData?.data ?? startData;
        livekitUrl = payload?.livekit_url;
        livekitToken = payload?.livekit_client_token;
        sessionIdRef.current = payload?.session_id ?? null;
      }

      if (!livekitUrl || !livekitToken) {
        updateState("error");
        throw new Error("Missing LiveKit credentials from session start");
      }

      if (roomRef.current) {
        try {
          roomRef.current.disconnect();
        } catch {
          /* ignore */
        }
        roomRef.current = null;
      }

      const room = new Room();
      roomRef.current = room;

      const aggregateStream = new MediaStream();
      let resolved = false;
      let rejectMediaReady: ((err: Error) => void) | null = null;

      const mediaReady = new Promise<MediaStream>((resolve, reject) => {
        mediaResolverRef.current = resolve;
        rejectMediaReady = reject;
      });

      room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
        const identity = participant ? participant.identity : "?";
        if (track.kind !== "video" && track.kind !== "audio") return;
        if (track.kind === "audio" && identity !== AVATAR_IDENTITY && optionsRef.current.useByoLivekit) return;
        const mediaTrack = track.mediaStreamTrack;
        if (!mediaTrack) return;
        aggregateStream.addTrack(mediaTrack);
        if (aggregateStream.getVideoTracks().length > 0) {
          const next = new MediaStream(aggregateStream.getTracks());
          setMediaStream(next);
          if (!resolved) {
            resolved = true;
            updateState("connected");
            mediaResolverRef.current?.(next);
            mediaResolverRef.current = null;
          }
        }
      });

      room.on(RoomEvent.DataReceived, (data) => {
        try {
          const text = new TextDecoder().decode(data);
          const parsed = JSON.parse(text);
          const transcript = parsed?.text || parsed?.transcript || parsed?.message;
          if (transcript && typeof transcript === "string") {
            optionsRef.current.onMessage?.(transcript);
          }
        } catch {
          /* non-JSON */
        }
      });

      room.on(RoomEvent.Disconnected, () => {
        if (roomRef.current === room) {
          setMediaStream(null);
          updateState("idle");
        }
      });

      await room.connect(
        livekitUrl,
        livekitToken,
        optionsRef.current.useByoLivekit
          ? { rtcConfig: { iceTransportPolicy: "relay" } }
          : undefined,
      );

      const timeout = new Promise<never>((_, reject) => {
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            const err = new Error("Avatar did not send video in time. Please try again.");
            rejectMediaReady?.(err);
            reject(err);
          }
        }, 20_000);
      });

      await Promise.race([mediaReady, timeout]);

      if (openingText) {
        setTimeout(() => {
          sendTextInternal(openingText).catch(console.warn);
        }, 1500);
      }
    },
    [updateState, sendTextInternal],
  );

  const pauseSession = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    room.remoteParticipants.forEach((p) => {
      p.trackPublications.forEach((pub) => {
        const t = pub.track;
        if (t?.kind === "audio" && t.mediaStreamTrack) t.mediaStreamTrack.enabled = false;
      });
    });
  }, []);

  const resumeSession = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    room.remoteParticipants.forEach((p) => {
      p.trackPublications.forEach((pub) => {
        const t = pub.track;
        if (t?.kind === "audio" && t.mediaStreamTrack) t.mediaStreamTrack.enabled = true;
      });
    });
  }, []);

  const setMicEnabled = useCallback(async (enabled: boolean) => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.localParticipant.setMicrophoneEnabled(enabled);
    } catch (err: any) {
      const name = err?.name || "";
      let message = "Could not access your microphone.";
      if (name === "NotAllowedError") message = "Microphone permission denied.";
      else if (name === "NotFoundError") message = "No microphone was found on this device.";
      else if (name === "NotReadableError") message = "Your microphone is in use by another app.";
      optionsRef.current.onError?.(message);
      throw err;
    }
  }, []);

  const sendText = useCallback(
    async (text: string) => {
      await sendTextInternal(text);
    },
    [sendTextInternal],
  );

  const stopSession = useCallback(async () => {
    if (roomRef.current) {
      try {
        roomRef.current.disconnect();
      } catch (err) {
        console.error(err);
      }
      roomRef.current = null;
    }
    mediaResolverRef.current = null;
    if (sessionTokenRef.current) {
      try {
        await liveavatar.stopSession(sessionTokenRef.current);
      } catch (err) {
        console.error("[stream] error stopping server session:", err);
      }
      sessionTokenRef.current = null;
    }
    setMediaStream(null);
    updateState("idle");
  }, [updateState]);

  return {
    state,
    mediaStream,
    sessionToken: sessionTokenRef,
    sessionId: sessionIdRef,
    prepareSession,
    goLive,
    stopSession,
    pauseSession,
    resumeSession,
    setMicEnabled,
    sendText,
  };
}
